/**
 * =================================================================
 * ⚠️  DEPRECATED — no longer loaded.
 *
 * Replaced by `clarity-tracker.js` (Clarity Tracker), which posts to
 * https://clarity.marke-ter.online/api/tracker/*.
 *
 * The <script src="codeblue_analytics.js"> tag in layout/theme.liquid
 * is commented out. `codeblue_formTracking.js` depends on the global
 * `window.__ANALYTICS_TRACKER__` exposed here, so it will no-op
 * silently once this file stops loading. Safe to delete both once
 * we're confident the new tracker is stable.
 * =================================================================
 */

/**
 * =================================================================
 * 1. ScrollTrackerModule (Active Time and Scroll Session Tracking)
 * =================================================================
 */
class ScrollTrackerModule {
    #state = {
        scrollSessions: [],
        totalActiveTime: 0,
        sessionStartTime: Date.now(),
        lastActiveTime: Date.now(),
        isTabActive: true,
        isScrolling: false,
        scrollStartTime: null,
        scrollStartPosition: null,
        lastUpdateTime: Date.now(),
    };
    #scrollEndTimeout = null;

    constructor() {
        this.#bindEvents();
    }

    #getScrollPercentage() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        return scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
    }

    updateActiveTime() {
        const currentTime = Date.now();
        if (this.#state.isTabActive) {
            const timeDiff = Math.round((currentTime - this.#state.lastActiveTime) / 1000);
            if (timeDiff > 0) {
                this.#state.totalActiveTime += timeDiff;
                this.#state.lastActiveTime = currentTime;
            }
        } else {
            this.#state.lastActiveTime = currentTime;
        }
        this.#state.lastUpdateTime = currentTime;
    }

    #startScrollSession() {
        if (this.#state.isTabActive) {
            this.#state.isScrolling = true;
            this.#state.scrollStartTime = Date.now();
            this.#state.scrollStartPosition = this.#getScrollPercentage();
        }
    }

    #endScrollSession() {
        if (!this.#state.isScrolling || this.#state.scrollStartTime === null) return;

        const scrollEndPosition = this.#getScrollPercentage();
        const scrollEndTime = Date.now();
        const timeSpent = Math.round((scrollEndTime - this.#state.scrollStartTime) / 1000);

        if (this.#state.scrollStartPosition !== scrollEndPosition && timeSpent > 0) {
            this.#state.scrollSessions.push({
                scrollStart: this.#state.scrollStartPosition,
                scrollEnd: scrollEndPosition,
                timeSpent,
            });
        }

        this.#state.isScrolling = false;
        this.#state.scrollStartTime = null;
        this.#state.scrollStartPosition = null;
    }

    #handleScroll = () => {
        if (!this.#state.isTabActive) return;

        if (!this.#state.isScrolling) {
            this.#startScrollSession();
        }

        clearTimeout(this.#scrollEndTimeout);
        this.#scrollEndTimeout = setTimeout(() => {
            if (this.#state.isScrolling && this.#state.isTabActive) {
                this.#endScrollSession();
            }
        }, 150);
    };

    #handleVisibilityChange = () => {
        if (document.hidden) {
            this.updateActiveTime();
            this.#state.isTabActive = false;
            this.#endScrollSession();
        } else {
            this.#state.isTabActive = true;
            this.#state.lastActiveTime = Date.now();
        }
    };

    #handleWindowBlur = () => {
        this.updateActiveTime();
        this.#state.isTabActive = false;
        this.#endScrollSession();
    };

    #handleWindowFocus = () => {
        this.#state.isTabActive = true;
        this.#state.lastActiveTime = Date.now();
    };

    #handleBeforeUnload = () => {
        this.updateActiveTime();
        // The original code returned finalData, which is used by the main tracker.
        return {
            totalTime: this.#state.totalActiveTime,
            scrollTime: this.#state.scrollSessions
        };
    };

    #bindEvents() {
        window.addEventListener("scroll", this.#handleScroll, { passive: true });
        document.addEventListener("visibilitychange", this.#handleVisibilityChange);
        window.addEventListener("blur", this.#handleWindowBlur);
        window.addEventListener("focus", this.#handleWindowFocus);
        window.addEventListener("beforeunload", this.#handleBeforeUnload);
    }

    getSessionData() {
        this.updateActiveTime();
        return {
            totalTime: this.#state.totalActiveTime,
            scrollTime: [...this.#state.scrollSessions]
        };
    }

    destroy() {
        window.removeEventListener("scroll", this.#handleScroll);
        document.removeEventListener("visibilitychange", this.#handleVisibilityChange);
        window.removeEventListener("blur", this.#handleWindowBlur);
        window.removeEventListener("focus", this.#handleWindowFocus);
        window.removeEventListener("beforeunload", this.#handleBeforeUnload);
        clearTimeout(this.#scrollEndTimeout);
    }
}

/**
 * =================================================================
 * 2. MeaningfulClickTracker Module (Integrated with AnalyticsTracker)
 * =================================================================
 */
class MeaningfulClickTracker {
  #state = {
    clicks: [],
    totalClicks: 0,
    meaningfulClicks: 0,
    ignoredClicks: 0,
    meaningfulSelectors: [
      "button",
      "a",
      "input",
      "select",
      "textarea",
      "img",
      '[role="button"]',
      '[role="link"]',
      "[onclick]",
      "[data-click]",
      ".btn",
      ".button",
      ".clickable",
      '[type="submit"]',
      '[type="button"]',
      '[type="reset"]',
    ],
    ignoredSelectors: [
      "html",
      "body",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "div",
      "span",
      "ul",
      "ol",
      "li",
      "table",
      "tr",
      "td",
      "th",
      "header",
      "footer",
      "nav",
      "main",
      "section",
      "article",
      ".ignore-click",
      "[data-ignore-click]",
    ],
  };

  constructor() {
    this.#bindEvents();
  }

  #isMeaningfulClick(element) {
    // Check if element matches meaningful selectors
    for (const selector of this.#state.meaningfulSelectors) {
      if (element.matches(selector)) {
        return true;
      }
    }

    // Check if element has meaningful attributes
    if (
      element.hasAttribute("onclick") ||
      element.hasAttribute("data-click") ||
      element.getAttribute("role") === "button" ||
      element.getAttribute("role") === "link"
    ) {
      return true;
    }

    // Check if element is inside a meaningful parent
    let parent = element.parentElement;
    let depth = 0;
    while (parent && parent !== document.body && depth < 5) {
      for (const selector of this.#state.meaningfulSelectors) {
        if (parent.matches(selector)) {
          return true;
        }
      }
      parent = parent.parentElement;
      depth++;
    }

    // Check if element matches ignored selectors
    for (const selector of this.#state.ignoredSelectors) {
      if (element.matches(selector)) {
        return false;
      }
    }

    // Default: ignore if it's a generic container/text element
    const genericTags = [
      "DIV",
      "SPAN",
      "P",
      "LI",
      "TD",
      "TH",
      "H1",
      "H2",
      "H3",
      "H4",
      "H5",
      "H6",
    ];
    return !genericTags.includes(element.tagName);
  }

  #getElementText(element) {
    let text = element.textContent?.trim();

    // For inputs, get value instead of text content
    if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
      text = element.value || element.placeholder || text;
    }

    // For images, get alt text
    if (element.tagName === "IMG") {
      text = element.alt || element.title || "Image";
    }

    // For selects, get selected option text
    if (element.tagName === "SELECT") {
      const selectedOption = element.options[element.selectedIndex];
      text = selectedOption?.text || "Select";
    }

    return text ? text.substring(0, 100) : null;
  }

  #getImportantAttributes(element) {
    const importantAttrs = [
      "href",
      "src",
      "alt",
      "title",
      "type",
      "value",
      "name",
      "role",
      "placeholder",
      "onclick",
      "data-click",
      "aria-label",
    ];
    const attributes = {};

    importantAttrs.forEach((attr) => {
      const value = element.getAttribute(attr);
      if (value) {
        attributes[attr] = value;
      }
    });

    return Object.keys(attributes).length > 0 ? attributes : null;
  }

  #getInteractionType(element) {
    const tag = element.tagName.toLowerCase();
    const type = element.getAttribute("type");

    if (tag === "button") return "button_click";
    if (tag === "a") return "link_click";
    if (tag === "input") {
      if (type === "submit") return "form_submit";
      if (type === "button") return "button_click";
      if (type === "reset") return "form_reset";
      return "input_click";
    }
    if (tag === "select") return "dropdown_select";
    if (tag === "textarea") return "text_input";
    if (tag === "img") return "image_click";

    return "element_click";
  }

  #collectClickData(element) {
    return {
      element: {
        tag: element.tagName,
        id: element.id || null,
        className: element.className || null,
        text: this.#getElementText(element),
        attributes: this.#getImportantAttributes(element),
        timestamp: new Date().toISOString(),
      },
      interactionType: this.#getInteractionType(element),
    };
  }

  #handleClick = (event) => {
    this.#state.totalClicks++;

    if (this.#isMeaningfulClick(event.target)) {
      this.#state.meaningfulClicks++;
      const clickData = this.#collectClickData(event.target);
      this.#state.clicks.push(clickData);
    } else {
      this.#state.ignoredClicks++;
    }
  };

  #bindEvents() {
    document.addEventListener("click", this.#handleClick);
  }

  getClickData() {
    return {
      totalClicks: this.#state.totalClicks,
      meaningfulClicks: this.#state.meaningfulClicks,
      clicks: [...this.#state.clicks],
    };
  }

  reset() {
    this.#state.clicks = [];
    this.#state.totalClicks = 0;
    this.#state.meaningfulClicks = 0;
    this.#state.ignoredClicks = 0;
  }

  destroy() {
    document.removeEventListener("click", this.#handleClick);
  }
}

/**
 * =================================================================
 * 3. Updated AnalyticsTracker Core Class with Click Tracking
 * =================================================================
 */
class AnalyticsTracker {
  API_URL;
  #API_KEY;
  #NAMESPACE = "__ANALYTICS_TRACKER__";
  #STORAGE_PREFIX = "analytics_tracker_";
  #BLOCKED_COUNTRIES = ["PK", "Pakistan"];
  #scrollTracker = new ScrollTrackerModule();
  #clickTracker = new MeaningfulClickTracker(); // Add click tracker

  // Session and Page View State
  sessionId = null;
  currentPageViewId = null;
  currentUrl = window.location.href;
  sessionStartTime = Date.now();
  lastActivityTime = Date.now();
  pageViewStartTime = Date.now();
  totalClicks = 0;
  pagesViewed = 0;
  maxScrollDepth = 0;
  locationData = null;
  isTrackingAllowed = true;
  isScrollRestored = false;
  initialScrollDepth = 0;
  meaningfulScrollDepth = 0;

  // Timeouts and Intervals
  #sessionTimeout = 1800 * 1000;
  #maxSessionDuration = 1800 * 1000;
  #pageViewTimeout = 30 * 1000;
  #sessionTimeoutInterval = null;
  #sessionUpdateInterval = null;
  #pageViewInterval = null;
  #urlCheckInterval = null;

  // Handlers
  #clickHandler = null;
  #scrollActivityHandler = null;
  #mouseMoveHandler = null;
  #keypressHandler = null;
  #visibilityChangeHandler = null;
  #beforeUnloadHandler = null;
  #scrollHandler = null;
  #scrollTimeout = null;

  constructor(
    apiKey,
    apiUrl = "https://api.analytics.codeblue.agency/api/track"
  ) {
    this.#API_KEY = apiKey;
    this.API_URL = apiUrl;
    this._initLocation();
  }

  // --- Utility Methods ---

  #log(...args) {
    if (typeof console !== "undefined" && console.log) {
      // console.log("[AnalyticsTracker]", ...args);
    }
  }

  #logError(...args) {
    if (typeof console !== "undefined" && console.error) {
      console.error("[AnalyticsTracker]", ...args);
    }
  }

  #setStorage(key, value) {
    try {
      localStorage.setItem(this.#STORAGE_PREFIX + key, value);
    } catch (e) {
      this.#logError("Storage error:", e);
    }
  }

  #getStorage(key) {
    try {
      return localStorage.getItem(this.#STORAGE_PREFIX + key);
    } catch (e) {
      this.#logError("Storage error:", e);
      return null;
    }
  }

  #removeStorage(key) {
    try {
      localStorage.removeItem(this.#STORAGE_PREFIX + key);
    } catch (e) {
      this.#logError("Storage error:", e);
    }
  }

  // Make API_KEY accessible for form tracking
  get apiKey() {
    return this.#API_KEY;
  }

  isAllowed() {
    return this.isTrackingAllowed;
  }

  // --- Geolocation and Blocking (Refactored from _initLocation) ---

  #mapCountryName(code) {
    const map = {
      US: "United States",
      GB: "United Kingdom",
      CA: "Canada",
      AU: "Australia",
      DE: "Germany",
      FR: "France",
      IT: "Italy",
      ES: "Spain",
      IN: "India",
      PK: "Pakistan",
      BD: "Bangladesh",
      CN: "China",
      JP: "Japan",
      BR: "Brazil",
      MX: "Mexico",
      RU: "Russia",
      AE: "United Arab Emirates",
      SA: "Saudi Arabia",
      EG: "Egypt",
      ZA: "South Africa",
      NG: "Nigeria",
      KE: "Kenya",
      NL: "Netherlands",
      SE: "Sweden",
      NO: "Norway",
      DK: "Denmark",
      FI: "Finland",
      PL: "Poland",
      TR: "Turkey",
      KR: "South Korea",
      ID: "Indonesia",
      MY: "Malaysia",
      SG: "Singapore",
      TH: "Thailand",
      PH: "Philippines",
      VN: "Vietnam",
    };
    return map[code.toUpperCase()] || code;
  }

  #mapCountryCode(name) {
    const map = {
      "United States": "US",
      "United Kingdom": "GB",
      Canada: "CA",
      Australia: "AU",
      Germany: "DE",
      France: "FR",
      Italy: "IT",
      Spain: "ES",
      India: "IN",
      Pakistan: "PK",
      Bangladesh: "BD",
      China: "CN",
      Japan: "JP",
      Brazil: "BR",
      Mexico: "MX",
      Russia: "RU",
      "United Arab Emirates": "AE",
      "Saudi Arabia": "SA",
      Egypt: "EG",
      "South Africa": "ZA",
      Nigeria: "NG",
      Kenya: "KE",
      Netherlands: "NL",
      Sweden: "SE",
      Norway: "NO",
      Denmark: "DK",
      Finland: "FI",
      Poland: "PL",
      Turkey: "TR",
      "South Korea": "KR",
      Indonesia: "ID",
      Malaysia: "MY",
      Singapore: "SG",
      Thailand: "TH",
      Philippines: "PH",
      Vietnam: "VN",
    };
    return map[name] || "Unknown";
  }

  #isCountryBlocked(countryCode) {
    return countryCode
      ? this.#BLOCKED_COUNTRIES.some(
          (blocked) => blocked.toUpperCase() === countryCode.toUpperCase()
        )
      : false;
  }

  #getLocationForAPI() {
    return this.locationData
      ? {
          city: this.locationData.city,
          state: this.locationData.state,
          country: this.locationData.country_code,
          timezone: this.locationData.timezone,
          ip_address: this.locationData.ip_address,
        }
      : {};
  }

  async _fetchLocation(url) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      this.#logError("Location fetch failed for URL:", url, error);
    }
    return null;
  }

  async _initLocation() {
    try {
      let locationData = null;

      // Attempt 1: ipapi.co
      let data = await this._fetchLocation("https://ipapi.co/json/");
      if (data && data.country !== "Unknown") {
        locationData = {
          city: data.city || "Unknown",
          state: data.region || "Unknown",
          country: data.country_name || "Unknown",
          country_code: data.country || "Unknown",
          timezone:
            data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          ip_address: data.ip || "0.0.0.0",
        };
      }

      // Attempt 2: ipinfo.io (if first attempt failed or was insufficient)
      if (
        !locationData ||
        locationData.country === "Unknown" ||
        locationData.country_code === "Unknown"
      ) {
        data = await this._fetchLocation(
          "https://ipinfo.io/json?token=9c4b3ea2fea9f5"
        );
        if (data) {
          const code = data.country || "Unknown";
          const name = this.#mapCountryName(code);
          locationData = {
            city: data.city || "Unknown",
            state: data.region || "Unknown",
            country: name || "Unknown",
            country_code: code,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ip_address: data.ip || "0.0.0.0",
          };
        }
      }

      // Fallback default
      if (!locationData) {
        locationData = {
          city: "Unknown",
          state: "Unknown",
          country: "United States",
          country_code: "US",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ip_address: "0.0.0.0",
        };
        this.#log("Fallback default location used", locationData);
      }

      // Ensure country code/name consistency
      if (
        locationData.country_code === "Unknown" &&
        locationData.country !== "Unknown"
      ) {
        locationData.country_code = this.#mapCountryCode(locationData.country);
      }
      if (
        locationData.country === "Unknown" &&
        locationData.country_code !== "Unknown"
      ) {
        locationData.country = this.#mapCountryName(locationData.country_code);
      }

      this.locationData = locationData;
      this.#setStorage("location", JSON.stringify(locationData));

      if (this.#isCountryBlocked(this.locationData.country_code)) {
        this.isTrackingAllowed = false;
        window.__TRACKING_BLOCKED__ = true;
        this.#log(
          `Tracking blocked for country: ${this.locationData.country_code}`
        );
        return;
      }

      this.#log("Final location data ready", this.locationData);
      this.isTrackingAllowed && (await this.#init());
    } catch (error) {
      this.#logError("Unexpected error initializing location", error);
    }
  }

  // --- Session Management ---

  #getOrCreateSessionId() {
    const stored = this.#getStorage("session");
    const now = Date.now();
    const timeout = this.#sessionTimeout;

    if (stored) {
      try {
        const session = JSON.parse(stored);
        if (
          session &&
          typeof session.id === 'string' &&
          Number.isFinite(session.lastActivity) &&
          Number.isFinite(session.startTime)
        ) {
          // Check for session timeout or max duration
          if (
            now - session.lastActivity < timeout &&
            now - session.startTime < this.#maxSessionDuration
          ) {
            this.sessionStartTime = session.startTime; // CRITICAL FIX: Ensure it's set
            return session.id;
          }
        }
      } catch {
        this.#removeStorage("session");
      }
    }

    // Create new session ID
    const newSessionId = `sess_${now}_${Math.random().toString(36).substr(2, 9)}`;
    this.sessionStartTime = now; // CRITICAL FIX: Set in class instance
    this.#saveSession(newSessionId);
    return newSessionId;
  }

  #saveSession(sessionId) {
    this.#setStorage(
      "session",
      JSON.stringify({
        id: sessionId,
        lastActivity: this.lastActivityTime, // FIX: Use class property
        startTime: this.sessionStartTime     // FIX: Use class property
      })
    );
  }

  #updateSessionActivity() {
    this.lastActivityTime = Date.now();
    this.#saveSession(this.sessionId);
    this.#scrollTracker.updateActiveTime(); // Update active time upon activity
  }

  #getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua))
      return "tablet";
    if (
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      )
    )
      return "mobile";
    return "desktop";
  }

  #getBrowser() {
    const ua = navigator.userAgent;
    if (ua.includes("Firefox")) return "Firefox";
    if (ua.includes("Chrome")) return "Chrome";
    if (ua.includes("Safari")) return "Safari";
    if (ua.includes("Edge")) return "Edge";
    if (ua.includes("Opera")) return "Opera";
    return "Unknown";
  }

  #getOS() {
    const ua = navigator.userAgent;
    if (ua.includes("Windows")) return "Windows";
    if (ua.includes("Mac")) return "macOS";
    if (ua.includes("Linux")) return "Linux";
    if (ua.includes("Android")) return "Android";
    if (ua.includes("iOS")) return "iOS";
    return "Unknown";
  }

  async #initSession() {
    if (!this.isTrackingAllowed) return;

    this.#log("Initializing session...");
    try {
      this.sessionId = this.sessionId || this.#getOrCreateSessionId(); // Ensure ID is set
      this.lastActivityTime = Date.now(); // Reset activity time

      const locationData = this.#getLocationForAPI();
      const payload = {
        api_key: this.#API_KEY,
        session_id: this.sessionId,
        entry_url: window.location.href,
        referrer: document.referrer || null,
        device_type: this.#getDeviceType(),
        browser: this.#getBrowser(),
        os: this.#getOS(),
        city: locationData.city || "Unknown",
        state: locationData.state || "Unknown",
        country: locationData.country || "Unknown",
        timezone:
          locationData.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        ip_address: locationData.ip_address || null,
      };

      this.#log("Sending session payload", payload);
      const response = await fetch(`${this.API_URL}/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      response.ok
        ? this.#log("Session created successfully:", data)
        : this.#logError("Session creation failed:", data);
    } catch (error) {
      this.#logError("Error creating session:", error);
    }
  }

  async #updateSession() {
    if (!this.isTrackingAllowed || !this.sessionId) return;
    try {
      const duration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
      const cappedDuration = Math.min(duration, 1800); // 30 minutes cap

      const payload = {
        api_key: this.#API_KEY,
        session_id: this.sessionId,
        exit_url: window.location.href,
        duration: cappedDuration,
        total_clicks: this.totalClicks,
        pages_viewed: this.pagesViewed,
        bounce: this.pagesViewed <= 1,
      };

      await fetch(`${this.API_URL}/session`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      this.#log(
        `Session updated (duration: ${cappedDuration}s, clicks: ${this.totalClicks}, pages: ${this.pagesViewed})`
      );
    } catch (error) {
      this.#logError("Error updating session:", error);
    }
  }

  async #endSession() {
    if (!this.isTrackingAllowed) return;

    const duration = Math.floor((Date.now() - this.sessionStartTime) / 1000);
    const cappedDuration = Math.min(duration, 1800);
    await this.#updatePageView(); // Ensure final page view is sent

    try {
      await fetch(`${this.API_URL}/session`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          api_key: this.#API_KEY,
          session_id: this.sessionId,
          exit_url: window.location.href,
          duration: cappedDuration,
          total_clicks: this.totalClicks,
          pages_viewed: this.pagesViewed,
          bounce: this.pagesViewed <= 1,
        }),
      });
      this.#log("Session ended with duration:", cappedDuration);
      this.#clearPageViewCache(); // Clear cache on session end
    } catch (error) {
      this.#logError("Error ending session:", error);
    }
  }

  #checkSessionTimeout() {
    if (!this.isTrackingAllowed) return;

    this.#sessionTimeoutInterval = setInterval(() => {
      const inactiveTime = Date.now() - this.lastActivityTime;
      const sessionAge = Date.now() - this.sessionStartTime;

      if (
        inactiveTime > this.#sessionTimeout ||
        sessionAge > this.#maxSessionDuration
      ) {
        const reason =
          inactiveTime > this.#sessionTimeout
            ? "inactivity"
            : "max duration reached";
        this.#log(`Session expired due to ${reason}`);

        // End current session
        this.#endSession();

        // Start new session - RESET ALL SESSION VARIABLES
        this.#removeStorage("session");
        this.sessionId = this.#getOrCreateSessionId();
        this.sessionStartTime = Date.now(); // CRITICAL FIX: Reset in class
        this.lastActivityTime = Date.now(); // CRITICAL FIX: Reset activity
        this.totalClicks = 0;
        this.pagesViewed = 0;
        this.#initSession();
      }
    }, 30000); // FIX: Check every 30 seconds instead of 60
  }

  #startSessionUpdates() {
    if (!this.isTrackingAllowed) return;

    this.#log("Starting session updates every 10 seconds...");
    this.#sessionUpdateInterval = setInterval(async () => {
      if (document.visibilityState === "visible") {
        await this.#updateSession();
      }
    }, 10000);
  }

  // --- Page View Tracking ---

  #getScrollDepth() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrolled =
      (((window.pageYOffset || document.documentElement.scrollTop) +
        windowHeight) /
        documentHeight) *
      100;
    return Math.min(Math.round(scrolled), 100);
  }

  async #checkForRestoredScroll() {
    this.initialScrollDepth = this.#getScrollDepth();
    this.meaningfulScrollDepth = this.initialScrollDepth;

    if (this.initialScrollDepth > 10) {
      this.#log(
        `Browser scroll restoration detected: ${this.initialScrollDepth}%`
      );
      this.isScrollRestored = true;

      // Wait 100ms as per original logic
      await new Promise((resolve) => setTimeout(resolve, 100));

      const currentScroll = this.#getScrollDepth();
      if (Math.abs(currentScroll - this.initialScrollDepth) > 5) {
        this.#log(
          "Significant scroll change detected - treating as user activity"
        );
        this.isScrollRestored = false;
      }
    } else {
      this.isScrollRestored = false;
    }
  }

  #getPageViewCache() {
    const cached = this.#getStorage("page_view");
    if (!cached) return null;
    try {
      const data = JSON.parse(cached);
      // Only use cache if it belongs to the current session
      return data.sessionId === this.sessionId ? data : null;
    } catch {
      return null;
    }
  }

  #savePageViewCache(pageViewId) {
    const cache = {
      id: pageViewId,
      url: window.location.href,
      timestamp: Date.now(),
      startTime: this.pageViewStartTime,
      sessionId: this.sessionId,
    };
    this.#setStorage("page_view", JSON.stringify(cache));
  }

  #clearPageViewCache() {
    this.#removeStorage("page_view");
  }

  async #checkIfRefresh() {
    const currentUrl = window.location.href;
    const cachedPageView = this.#getPageViewCache();

    if (!cachedPageView) return false;

    const timeSinceLastView = Date.now() - cachedPageView.timestamp;

    if (
      cachedPageView.url === currentUrl &&
      timeSinceLastView < this.#pageViewTimeout
    ) {
      this.currentPageViewId = cachedPageView.id;
      this.pageViewStartTime = cachedPageView.startTime;
      return true; // Page refresh detected
    }
    return false;
  }

  #getCanonicalUrl(url) {
    try {
      const urlObj = new URL(url);
      const ignoredParams = [
        "_pos",
        "_sid",
        "_ss",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
        "gclid",
        "fbclid",
        "mc_cid",
        "mc_eid",
        "_ga",
        "gbraid",
        "wbraid",
        "variant",
        "filter",
        "sort_by",
        "page",
        "fb_action_ids",
        "fb_action_types",
        "fb_ref",
        "fb_source",
        "yclid",
        "msclkid",
        "igshid",
        "ttclid",
      ];
      const params = new URLSearchParams(urlObj.search);
      ignoredParams.forEach((param) => params.delete(param));
      urlObj.hash = "";
      return `${urlObj.origin}${urlObj.pathname}${
        params.toString() ? "?" + params.toString() : ""
      }`;
    } catch {
      return url
        .replace(/[?&]_(pos|sid|ss)=[^&]+/g, "")
        .replace(/[?&]utm_[^&]+/g, "")
        .replace(/[?&]gclid=[^&]+/g, "")
        .replace(/[?&]fbclid=[^&]+/g, "")
        .replace(/#.*$/g, "")
        .replace(/\?$/g, "");
    }
  }

  #shouldTrackAsNewPage(previousUrl, currentUrl) {
    try {
      const prevUrl = new URL(previousUrl);
      const currUrl = new URL(currentUrl);

      // Path change is always a new page
      if (prevUrl.pathname !== currUrl.pathname) return true;

      // Check for meaningful parameter changes
      const meaningfulParams = ["size", "color", "quantity", "view", "layout"];
      const prevParams = new URLSearchParams(prevUrl.search);
      const currParams = new URLSearchParams(currUrl.search);

      for (let param of meaningfulParams) {
        if (prevParams.get(param) !== currParams.get(param)) return true;
      }

      // Check for variant change (explicitly handled in original code)
      const prevVariant = prevParams.get("variant");
      const currVariant = currParams.get("variant");
      return prevVariant !== currVariant;
    } catch {
      // Fallback for invalid URLs: check path part
      const getPath = (url) => {
        try {
          return new URL(url).pathname;
        } catch {
          return url.split("?")[0].split("#")[0];
        }
      };
      return getPath(previousUrl) !== getPath(currentUrl);
    }
  }

  #detectUrlChanges() {
    if (!this.isTrackingAllowed) return;

    let previousUrl = this.#getCanonicalUrl(window.location.href);

    this.#urlCheckInterval = setInterval(() => {
      const currentUrl = this.#getCanonicalUrl(window.location.href);
      if (currentUrl !== previousUrl) {
        if (this.#shouldTrackAsNewPage(previousUrl, currentUrl)) {
          this.#log("Significant URL change detected, creating new page view");
          previousUrl = currentUrl;
          this.pagesViewed++;
          this.pageViewStartTime = Date.now();
          this.#clearPageViewCache();
          this.#createPageView();
        } else {
          this.#log("Ignoring parameter-only URL change", {
            from: previousUrl,
            to: currentUrl,
          });
          previousUrl = currentUrl;
        }
      }
    }, 500);
  }

  // --- Updated Page View Methods with Click Tracking ---

  async #createPageView() {
    if (!this.isTrackingAllowed) return;
    try {
      const scrollDepth = this.isScrollRestored ? 0 : this.#getScrollDepth();
      const scrollActivity = this.#scrollTracker.getSessionData();
      const clickData = this.#clickTracker.getClickData(); // Get click data

      const payload = {
        api_key: this.#API_KEY,
        session_id: this.sessionId,
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
        scroll_depth: scrollDepth,
        time_on_page: 0,
        clicks: clickData.totalClicks, // Use meaningful click count
        scroll_activity: scrollActivity,
        clicks_data: clickData, // Add detailed click data
      };

      const response = await fetch(`${this.API_URL}/pageview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      this.currentPageViewId = data.data?.id;
      this.currentUrl = window.location.href;
      this.#savePageViewCache(this.currentPageViewId);

      // this.#log("Page view created:", {
      //   scroll_depth: scrollDepth,
      //   is_scroll_restored: this.isScrollRestored,
      //   meaningful_clicks: clickData.meaningfulClicks,
      // });
    } catch (error) {
      this.#logError("Error creating page view:", error);
    }
  }

  async #updatePageView() {
    if (!this.isTrackingAllowed || !this.currentPageViewId) return;

    try {
      const timeOnPage = Math.floor(
        (Date.now() - this.pageViewStartTime) / 1000
      );
      const scrollDepth = this.meaningfulScrollDepth;
      const scrollActivity = this.#scrollTracker.getSessionData();
      const clickData = this.#clickTracker.getClickData(); // Get updated click data

      const payload = {
        api_key: this.#API_KEY,
        scroll_depth: scrollDepth,
        time_on_page: timeOnPage,
        clicks: clickData.totalClicks, // Use meaningful click count
        scroll_activity: scrollActivity,
        clicks_data: clickData, // Add detailed click data
      };

      await fetch(`${this.API_URL}/pageview/${this.currentPageViewId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      this.#log(
        `Page view updated (time: ${timeOnPage}s, scroll: ${scrollDepth}%, clicks: ${clickData.meaningfulClicks})`
      );
    } catch (error) {
      this.#logError("Error updating page view:", error);
    }
  }

  #startPageViewUpdates() {
    if (!this.isTrackingAllowed) return;
    this.#pageViewInterval = setInterval(() => {
      if (document.visibilityState === "visible") {
        this.#updatePageView();
      }
    }, 10000);
  }

  async #initPageViewTracking() {
    if (!this.isTrackingAllowed) return;
    this.#log("Initializing page view tracking...");

    await this.#checkForRestoredScroll();

    if (await this.#checkIfRefresh()) {
      this.#log("Page refresh detected - updating existing page view");
    } else {
      this.#log("New page view detected");
      this.pagesViewed++;
      this.pageViewStartTime = Date.now();
      await this.#createPageView();
    }

    this.#initScrollTracking();
    this.#startPageViewUpdates();
    this.#detectUrlChanges();
  }

  // --- Updated Activity Tracking ---

  #initActivityTracking() {
    if (!this.isTrackingAllowed) return;
    this.#log("Initializing activity tracking...");

    // Click handler for session activity (keeps existing behavior)
    this.#clickHandler = () => {
      this.totalClicks++;
      this.#updateSessionActivity();
      this.#log(`Click tracked (total: ${this.totalClicks})`);
    };

    // Other activity handlers remain the same
    this.#scrollActivityHandler = () => {
      this.#updateSessionActivity();
    };

    let mouseMoveTimeout;
    this.#mouseMoveHandler = () => {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = setTimeout(() => {
        this.#updateSessionActivity();
      }, 1000);
    };

    this.#keypressHandler = () => {
      this.#updateSessionActivity();
    };

    this.#visibilityChangeHandler = () => {
      if (document.visibilityState === "hidden") {
        this.#updatePageView();
        this.#endSession();
      } else {
        this.#updateSessionActivity();
      }
    };

    document.addEventListener("click", this.#clickHandler);
    document.addEventListener("scroll", this.#scrollActivityHandler);
    document.addEventListener("mousemove", this.#mouseMoveHandler);
    document.addEventListener("keypress", this.#keypressHandler);
    document.addEventListener(
      "visibilitychange",
      this.#visibilityChangeHandler
    );

    this.#log("Activity tracking initialized");
  }

  // --- Activity Tracking (Click, Scroll, etc.) ---

  #initScrollTracking() {
    this.maxScrollDepth = this.initialScrollDepth;
    this.meaningfulScrollDepth = this.initialScrollDepth;

    this.#scrollHandler = () => {
      if (this.#scrollTimeout) return;

      this.#scrollTimeout = setTimeout(() => {
        const currentScroll = this.#getScrollDepth();

        if (currentScroll > this.maxScrollDepth) {
          this.maxScrollDepth = currentScroll;

          // Logic to detect meaningful scroll after restoration
          if (
            this.isScrollRestored &&
            Math.abs(currentScroll - this.initialScrollDepth) > 5
          ) {
            this.#log("User scroll activity detected after restoration");
            this.isScrollRestored = false;
          }

          // Update meaningful depth only if not restored, or if restored and scroll increased past initial
          if (
            !this.isScrollRestored ||
            currentScroll > this.initialScrollDepth
          ) {
            this.meaningfulScrollDepth = currentScroll;
          }
        }

        this.#scrollTimeout = null;
      }, 100);
    };

    window.addEventListener("scroll", this.#scrollHandler, { passive: true });
  }

  #initBeforeUnload() {
    if (!this.isTrackingAllowed) return;

    this.#beforeUnloadHandler = () => {
      this.#updatePageView();
      this.#endSession();
    };

    window.addEventListener("beforeunload", this.#beforeUnloadHandler);
  }

  // --- Initialization and Cleanup ---

  async #init() {
    if (!this.isTrackingAllowed) return;

    this.#log("🚀 Initializing all tracking components...");
    
    // Validate current session first
    const currentTime = Date.now();
    const storedSession = this.#getStorage("session");
    if (storedSession) {
      try {
        const session = JSON.parse(storedSession);
        if (currentTime - session.startTime > this.#maxSessionDuration) {
          this.#log("Existing session exceeded max duration, creating new one");
          this.#removeStorage("session");
        }
      } catch (e) {
        this.#removeStorage("session");
      }
    }
    
    this.sessionId = this.#getOrCreateSessionId();
    await this.#initSession();
    await this.#initPageViewTracking();
    this.#initActivityTracking();
    this.#initBeforeUnload();
    this.#checkSessionTimeout();
    this.#startSessionUpdates();
    this.#log("✅ All tracking components initialized");
  }

  // --- Updated Cleanup Method ---

  destroy() {
    // Clear intervals/timeouts
    if (this.#sessionTimeoutInterval)
      clearInterval(this.#sessionTimeoutInterval);
    if (this.#sessionUpdateInterval) clearInterval(this.#sessionUpdateInterval);
    if (this.#pageViewInterval) clearInterval(this.#pageViewInterval);
    if (this.#urlCheckInterval) clearInterval(this.#urlCheckInterval);
    if (this.#scrollTimeout) clearTimeout(this.#scrollTimeout);

    // Remove listeners
    if (this.#scrollHandler)
      window.removeEventListener("scroll", this.#scrollHandler);
    if (this.#clickHandler)
      document.removeEventListener("click", this.#clickHandler);
    if (this.#scrollActivityHandler)
      document.removeEventListener("scroll", this.#scrollActivityHandler);
    if (this.#mouseMoveHandler)
      document.removeEventListener("mousemove", this.#mouseMoveHandler);
    if (this.#keypressHandler)
      document.removeEventListener("keypress", this.#keypressHandler);
    if (this.#visibilityChangeHandler)
      document.removeEventListener(
        "visibilitychange",
        this.#visibilityChangeHandler
      );
    if (this.#beforeUnloadHandler)
      window.removeEventListener("beforeunload", this.#beforeUnloadHandler);

    // Destroy trackers
    this.#scrollTracker.destroy();
    this.#clickTracker.destroy(); // Destroy click tracker

    this.#log("Analytics tracker destroyed");
  }
}

/**
 * =================================================================
 * Global Setup and Exposure (Unchanged)
 * =================================================================
 */
((globalWindow) => {
  const NAMESPACE = "__ANALYTICS_TRACKER__";

  globalWindow[NAMESPACE] = {
    AnalyticsTracker: AnalyticsTracker,
    version: "2.1.1", // Bumped version for session fix
    init: (apiKey, apiUrl) => {
      if (!globalWindow[NAMESPACE].instance) {
        globalWindow[NAMESPACE].instance = new AnalyticsTracker(apiKey, apiUrl);
      }
      return globalWindow[NAMESPACE].instance;
    },
    getInstance: () => globalWindow[NAMESPACE].instance || null,
  };

  if (globalWindow.ANALYTICS_API_KEY) {
    globalWindow[NAMESPACE].init(globalWindow.ANALYTICS_API_KEY);
    globalWindow.analytics = globalWindow[NAMESPACE].instance;
  }
})(window);